import "./styles.css";

import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";

// The type of messages we'll be receiving from the server
import type { OutgoingMessage } from "../shared";
import type { LegacyRef } from "react";

// Image URLs from CLAUDE.md
const imageUrls = [
  "https://storage-spendo.monzim.com/dhon-pay/dhon-cards-webp/Touch%20Korei%20Deya%20Jabe.webp",
  "https://storage-spendo.monzim.com/dhon-pay/dhon-cards-webp/Dhon%20Pay%20Meme%20Marketing.webp",
  "https://storage-spendo.monzim.com/dhon-pay/dhon-cards-webp/Dhon%20Din%20Shojei.webp",
  "https://storage-spendo.monzim.com/dhon-pay/dhon-cards-webp/WebP%20converter%20image%20(3).webp",
  "https://storage-spendo.monzim.com/dhon-pay/dhon-cards-webp/Dhon%20Lange%20Ache%20Dhone.webp",
  "https://storage-spendo.monzim.com/dhon-pay/dhon-cards-webp/WebP%20Converter%20Image%20(2).webp",
  // "https://storage-spendo.monzim.com/dhon-pay/dhon-cards-webp/Kim%20Jong%20Un%20Image.webp",
];

function ImageCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareImageIndex, setShareImageIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % imageUrls.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const shareImage = async (index: number) => {
    const imageUrl = imageUrls[index];
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Dhon Din Shojei Card Design ${index + 1}`,
          text: "Check out this beautiful Dhon Pay card design!",
          url: imageUrl,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(imageUrl);
        alert("Image URL copied to clipboard!");
      } catch (error) {
        // Final fallback - open share modal
        setShareImageIndex(index);
        setIsShareOpen(true);
      }
    }
  };

  const downloadImage = async (index: number) => {
    try {
      const response = await fetch(imageUrls[index]);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dhon-din-shojei-${index + 1}.webp`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div className="carousel-container">
      <div className="carousel-main">
        <button className="carousel-btn carousel-btn-prev" onClick={prevSlide}>
          <svg className="carousel-icon" viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </button>

        <div className="carousel-image-container">
          <img
            src={imageUrls[currentIndex]}
            alt={`Dhon Din Shojei ${currentIndex + 1}`}
            className="carousel-image"
          />
          <div className="carousel-actions">
            <button
              className="carousel-action-btn"
              onClick={() => shareImage(currentIndex)}
              title="Share"
            >
              <svg className="carousel-action-icon" viewBox="0 0 24 24">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </button>
            <button
              className="carousel-action-btn"
              onClick={() => downloadImage(currentIndex)}
              title="Download"
            >
              <svg className="carousel-action-icon" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7,10 12,15 17,10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          </div>
        </div>

        <button className="carousel-btn carousel-btn-next" onClick={nextSlide}>
          <svg className="carousel-icon" viewBox="0 0 24 24">
            <polyline points="9,18 15,12 9,6" />
          </svg>
        </button>
      </div>

      <div className="carousel-thumbnails">
        {imageUrls.map((url, index) => (
          <button
            key={index}
            className={`carousel-thumbnail ${
              index === currentIndex ? "active" : ""
            }`}
            onClick={() => goToSlide(index)}
          >
            <img src={url} alt={`Thumbnail ${index + 1}`} />
          </button>
        ))}
      </div>

      <div className="carousel-indicators">
        {imageUrls.map((_, index) => (
          <button
            key={index}
            className={`carousel-indicator ${
              index === currentIndex ? "active" : ""
            }`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>

      {/* Share Modal */}
      {isShareOpen && (
        <div
          className="share-modal-overlay"
          onClick={() => setIsShareOpen(false)}
        >
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Share Image</h3>
              <button onClick={() => setIsShareOpen(false)}>×</button>
            </div>
            <div className="share-modal-content">
              <p>Copy the image URL to share:</p>
              <div className="share-url-container">
                <input
                  type="text"
                  value={imageUrls[shareImageIndex]}
                  readOnly
                  className="share-url-input"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(imageUrls[shareImageIndex]);
                    setIsShareOpen(false);
                  }}
                  className="share-copy-btn"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  // A reference to the canvas element where we'll render the globe
  const canvasRef = useRef<HTMLCanvasElement>();
  // The number of markers we're currently displaying
  const [counter, setCounter] = useState(0);
  // Form states
  const [wishlistEmail, setWishlistEmail] = useState("");
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [wishlistSuccess, setWishlistSuccess] = useState(false);
  const [ambassadorLoading, setAmbassadorLoading] = useState(false);
  const [ambassadorSuccess, setAmbassadorSuccess] = useState(false);
  const [ambassadorFormOpen, setAmbassadorFormOpen] = useState(false);
  const [ambassadorForm, setAmbassadorForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [visitorTracked, setVisitorTracked] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // A map of marker IDs to their positions
  // Note that we use a ref because the globe's `onRender` callback
  // is called on every animation frame, and we don't want to re-render
  // the component on every frame.
  const positions = useRef<
    Map<
      string,
      {
        location: [number, number];
        size: number;
      }
    >
  >(new Map());
  // Connect to the PartyServer server
  const socket = usePartySocket({
    room: "default",
    party: "globe",
    onMessage(evt) {
      const message = JSON.parse(evt.data as string) as OutgoingMessage;
      if (message.type === "add-marker") {
        // Add the marker to our map
        positions.current.set(message.position.id, {
          location: [message.position.lat, message.position.lng],
          size: message.position.id === socket.id ? 0.1 : 0.05,
        });
        // Update the counter
        setCounter((c) => c + 1);
      } else {
        // Remove the marker from our map
        positions.current.delete(message.id);
        // Update the counter
        setCounter((c) => c - 1);
      }
    },
  });

  useEffect(() => {
    // Hide loading screen when app loads
    document.body.classList.add("app-loaded");

    // Track visitor on first load
    if (!visitorTracked) {
      fetch("/api/visitor", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      }).catch(console.error);
      setVisitorTracked(true);
    }

    // The angle of rotation of the globe
    // We'll update this on every frame to make the globe spin
    let phi = 0;

    const globe = createGlobe(canvasRef.current as HTMLCanvasElement, {
      devicePixelRatio: 2,
      width: 400 * 2,
      height: 400 * 2,
      phi: 0,
      theta: 0,
      dark: 1,
      diffuse: 0.8,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.3, 0.3, 0.3],
      markerColor: [0.8, 0.1, 0.1],
      glowColor: [0.2, 0.2, 0.2],
      markers: [],
      opacity: 0.7,
      onRender: (state) => {
        // Called on every animation frame.
        // `state` will be an empty object, return updated params.

        // Get the current positions from our map
        state.markers = [...positions.current.values()];

        // Rotate the globe
        state.phi = phi;
        phi += 0.01;
      },
    });

    return () => {
      globe.destroy();
    };
  }, [visitorTracked]);

  const scrollToGlobe = () => {
    document.getElementById("globe-section")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const showNotification = (message: string, type: "success" | "error") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleWishlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wishlistEmail.includes("@")) {
      showNotification("Please enter a valid email address", "error");
      return;
    }

    setWishlistLoading(true);
    try {
      const response = await fetch("/api/wishlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: wishlistEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setWishlistSuccess(true);
        setWishlistEmail("");
        showNotification(
          "Successfully joined the wishlist! We'll be in touch soon.",
          "success"
        );
      } else {
        showNotification(data.error || "Failed to join wishlist", "error");
      }
    } catch (error) {
      showNotification("Failed to join wishlist. Please try again.", "error");
    }
    setWishlistLoading(false);
  };

  const handleAmbassadorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAmbassadorLoading(true);

    try {
      const response = await fetch("/api/ambassador", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ambassadorForm),
      });

      const data = await response.json();

      if (response.ok) {
        setAmbassadorSuccess(true);
        setAmbassadorFormOpen(false);
        setAmbassadorForm({ name: "", email: "", message: "" });
        showNotification(
          "Thank you for your application! We'll be in touch soon.",
          "success"
        );
      } else {
        showNotification(data.error || "Failed to submit application", "error");
      }
    } catch (error) {
      showNotification(
        "Failed to submit application. Please try again.",
        "error"
      );
    }
    setAmbassadorLoading(false);
  };

  return (
    <>
      {/* Notification Toast */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          <div className="notification-content">
            <svg className="notification-icon" viewBox="0 0 24 24">
              {notification.type === "success" ? (
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              ) : (
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              )}
              {notification.type === "success" ? (
                <polyline points="22,4 12,14.01 9,11.01" />
              ) : (
                <>
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </>
              )}
            </svg>
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="notification-close"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <a href="/" className="font-mono">
                <div className="flex items-center">
                  <span className="logo-text">Dhon</span>
                  <span
                    style={{
                      marginLeft: "0.5rem",
                    }}
                  >
                    <span className="logo-text-muted">Pay</span>
                  </span>
                </div>
              </a>
            </div>

            <nav className="header-nav">
              <a href="/team" className="nav-link">
                Team
              </a>
              {counter !== 0 ? (
                <p className="global-vx">
                  <b>{counter}</b> {counter === 1 ? "person" : "people"}{" "}
                  connected
                </p>
              ) : (
                <p className="global-vx">&nbsp;</p>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Floating Visitor Count for Mobile */}
      {counter !== 0 && (
        <div className="floating-visitor-count">
          <div className="floating-count-content">
            <div className="floating-count-dot"></div>
            <span className="floating-count-text">
              <b>{counter}</b> {counter === 1 ? "person" : "people"} online
            </span>
          </div>
        </div>
      )}

      <section className="hero">
        <div className="container">
          <div className="badge">
            <div className="badge-dot"></div>
            <span style={{ color: "var(--muted-foreground)" }}>
              Coming Soon
            </span>
          </div>

          <h1 className="hero-title">
            The Future of
            <span className="gradient-text"> Digital Payments</span>
          </h1>

          <p className="hero-description">
            Simple, secure, and seamless transactions at your fingertips.
            Experience the next generation of digital wallet technology.
          </p>

          <div className="card" style={{ position: "relative" }}>
            <div className="card-content">
              <div className="card-header">
                <span className="card-title font-mono">Dhon Pay</span>
                <div className="card-dots">
                  <div className="dot pulse"></div>
                  <div className="dot pulse"></div>
                  <div className="dot pulse"></div>
                </div>
              </div>

              <div className="balance-section">
                <p className="balance-label">Available Balance</p>
                <p className="balance-amount font-mono">71,585.74</p>
              </div>

              <div className="card-icon">
                <svg
                  className="icon"
                  viewBox="0 0 24 24"
                  style={{ color: "white" }}
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
            </div>
          </div>

          <form onSubmit={handleWishlistSubmit} className="wishlist-form-hero">
            <input
              type="email"
              value={wishlistEmail}
              onChange={(e) => setWishlistEmail(e.target.value)}
              placeholder="Enter your email address"
              className="input-hero"
              required
              disabled={wishlistLoading || wishlistSuccess}
            />
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={wishlistLoading || wishlistSuccess}
            >
              {wishlistLoading
                ? "Joining..."
                : wishlistSuccess
                ? "Joined!"
                : "Join Wishlist"}
              {!wishlistLoading && !wishlistSuccess && (
                <svg
                  className="icon"
                  viewBox="0 0 24 24"
                  style={{
                    marginLeft: "0.5rem",
                    width: "1rem",
                    height: "1rem",
                  }}
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12,5 19,12 12,19" />
                </svg>
              )}
            </button>
          </form>

          <button className="btn btn-lg btn-secondary" onClick={scrollToGlobe}>
            View Globe
            <svg
              className="icon"
              viewBox="0 0 24 24"
              style={{
                marginLeft: "0.5rem",
                width: "1rem",
                height: "1rem",
              }}
            >
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
            </svg>
          </button>
        </div>
      </section>

      <section className="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Powerful Features for Modern Banking
            </h2>
            <p className="section-description">
              Dhon Pay is packed with innovative features designed to make your
              financial life easier and more secure.
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg className="icon" viewBox="0 0 24 24">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <h3 className="feature-title">Tap to Pay</h3>
              <p className="feature-description">
                Seamlessly pay with a tap using your UIU ID card. Quick, easy,
                and secure transactions.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg className="icon" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <rect x="7" y="7" width="4" height="4" />
                  <rect x="7" y="13" width="4" height="4" />
                  <rect x="13" y="7" width="4" height="4" />
                  <rect x="13" y="13" width="4" height="4" />
                </svg>
              </div>
              <h3 className="feature-title">QR Payment</h3>
              <p className="feature-description">
                Scan QR codes for fast and convenient payments at your favorite
                stores and vendors.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M1 4v16l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H3a2 2 0 0 0-2 2z" />
                  <path d="M11 2a2 2 0 0 1 2 2v16l-4-4H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h6z" />
                </svg>
              </div>
              <h3 className="feature-title">Auto Pay</h3>
              <p className="feature-description">
                Set up recurring payments for your bills and subscriptions.
                Never miss a due date again.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg className="icon" viewBox="0 0 24 24">
                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                </svg>
              </div>
              <h3 className="feature-title">Rewards Points</h3>
              <p className="feature-description">
                Earn points on every transaction and redeem them for exciting
                rewards and discounts.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3 className="feature-title">Enhanced Security</h3>
              <p className="feature-description">
                Advanced encryption and security protocols keep your data and
                money safe at all times.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </div>
              <h3 className="feature-title">Anonymous Paying</h3>
              <p className="feature-description">
                Make payments without revealing your identity. Your privacy is
                our top priority.
              </p>
            </div>
          </div>

          <div className="smart-borrowing">
            <div className="smart-card">
              <div className="smart-icon">
                <svg className="icon-lg" viewBox="0 0 24 24">
                  <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1z" />
                  <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h13a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1H5a2 2 0 0 0-2 2v2z" />
                </svg>
              </div>
              <h3
                style={{
                  fontSize: "1.875rem",
                  fontWeight: "700",
                  marginBottom: "1rem",
                }}
              >
                Smart Borrowing
              </h3>
              <p
                style={{
                  fontSize: "1.125rem",
                  color: "var(--muted-foreground)",
                  lineHeight: "1.7",
                  maxWidth: "32rem",
                  margin: "0 auto",
                  fontWeight: "300",
                }}
              >
                Revolutionary lending system with automatic repayment. Set
                return limits and let our smart system handle transfers when
                funds are available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wishlist Section */}
      <section className="wishlist">
        <div className="container">
          <div className="wishlist-card">
            <div className="feature-icon" style={{ marginBottom: "1.5rem" }}>
              <svg className="icon" viewBox="0 0 24 24">
                <polyline points="20,12 20,22 4,22 4,12" />
                <rect x="2" y="7" width="20" height="5" />
                <line x1="12" y1="22" x2="12" y2="7" />
                <path d="M12 7L7.5 2.21a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l1.67 1.67 1.67-1.67a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83z" />
              </svg>
            </div>

            <h2
              style={{
                fontSize: "1.875rem",
                fontWeight: "700",
                marginBottom: "1rem",
              }}
            >
              Join the Wishlist
            </h2>
            <p
              style={{
                marginBottom: "2rem",
                fontSize: "1.125rem",
                color: "var(--muted-foreground)",
                fontWeight: "300",
                lineHeight: "1.7",
              }}
            >
              Be the first to know when Dhon Pay launches. Get exclusive early
              access and special perks.
            </p>

            {!wishlistSuccess ? (
              <form onSubmit={handleWishlistSubmit} className="wishlist-form">
                <input
                  type="email"
                  value={wishlistEmail}
                  onChange={(e) => setWishlistEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="input"
                  required
                  disabled={wishlistLoading}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={wishlistLoading}
                >
                  {wishlistLoading ? "Joining..." : "Notify Me"}
                </button>
              </form>
            ) : (
              <div className="success-message">
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22,4 12,14.01 9,11.01" />
                </svg>
                <span>You're on the list! We'll be in touch soon.</span>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="ambassador">
        <div className="container">
          <div className="ambassador-card">
            <div className="smart-icon">
              <svg className="icon-lg" viewBox="0 0 24 24">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>

            <h2
              style={{
                marginBottom: "1.5rem",
                fontSize: "1.875rem",
                fontWeight: "700",
              }}
            >
              Become a Dhon Pay Ambassador
            </h2>
            <p
              style={{
                marginBottom: "2rem",
                fontSize: "1.125rem",
                color: "var(--muted-foreground)",
                lineHeight: "1.7",
                maxWidth: "32rem",
                marginLeft: "auto",
                marginRight: "auto",
                fontWeight: "300",
              }}
            >
              Love what we're building? Join our Ambassador Program to help
              spread the word. Get rewarded with exclusive merchandise, early
              feature access, and more.
            </p>

            <button
              className="btn btn-primary btn-lg"
              onClick={() => setAmbassadorFormOpen(true)}
              disabled={ambassadorSuccess}
            >
              {ambassadorSuccess ? "Application Submitted!" : "Apply Now"}
              {!ambassadorSuccess && (
                <svg
                  className="icon"
                  viewBox="0 0 24 24"
                  style={{
                    marginLeft: "0.5rem",
                    width: "1rem",
                    height: "1rem",
                  }}
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12,5 19,12 12,19" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Ambassador Modal */}
      {ambassadorFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Ambassador Application</h3>
              <button
                className="modal-close"
                onClick={() => setAmbassadorFormOpen(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAmbassadorSubmit} className="ambassador-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={ambassadorForm.name}
                  onChange={(e) =>
                    setAmbassadorForm({
                      ...ambassadorForm,
                      name: e.target.value,
                    })
                  }
                  className="form-input"
                  required
                  disabled={ambassadorLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ambassador-email">Email</label>
                <input
                  type="email"
                  id="ambassador-email"
                  value={ambassadorForm.email}
                  onChange={(e) =>
                    setAmbassadorForm({
                      ...ambassadorForm,
                      email: e.target.value,
                    })
                  }
                  className="form-input"
                  disabled={ambassadorLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  Why do you want to be an ambassador?
                </label>
                <textarea
                  id="message"
                  value={ambassadorForm.message}
                  onChange={(e) =>
                    setAmbassadorForm({
                      ...ambassadorForm,
                      message: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows={4}
                  placeholder="Tell us about your passion for fintech, social media presence, or how you'd promote Dhon Pay..."
                  disabled={ambassadorLoading}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-lg"
                  onClick={() => setAmbassadorFormOpen(false)}
                  disabled={ambassadorLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={ambassadorLoading}
                >
                  {ambassadorLoading ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section id="globe-section" className="globe-section">
        <div className="container">
          <h2 className="globe-title">Where's everyone at?</h2>
          {counter !== 0 ? (
            <p className="globe-counter">
              <b>{counter}</b> {counter === 1 ? "person" : "people"} connected.
            </p>
          ) : (
            <p className="globe-counter">&nbsp;</p>
          )}

          <canvas
            ref={canvasRef as LegacyRef<HTMLCanvasElement>}
            className="globe-canvas"
          />
        </div>
      </section>

      {/* Image Gallery Carousel Section */}
      <section className="gallery">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Dhon Din Shojei Collection</h2>
            <p className="section-description">
              Explore our beautiful collection of Dhon Pay card designs. Share
              these stunning visuals with your friends and family.
            </p>
          </div>
          <ImageCarousel />
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              {/* <svg className="logo-icon icon" viewBox="0 0 24 24">
                <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1z" />
                <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h13a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1H5a2 2 0 0 0-2 2v2z" />
              </svg> */}
              <img
                src="/apple-touch-icon.png"
                alt="logo"
                className="logo-icon icon"
              />
              <span className="logo-text font-mono">Dhon Pay</span>
            </div>

            <p className="footer-tagline">Simple. Secure. Seamless.</p>
            <p className="footer-copyright">
              © 2024 Dhon Pay | Spendo AI. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<App />);
